import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from "react-toastify";
import { canModuleEdit } from '../Function/Permission';

// Helper to extract GPS Coordinates from text / user data
const parseGpsLocation = (locationStr) => {
  if (!locationStr || typeof locationStr !== 'string') return null;
  const str = locationStr.trim();
  if (!str) return null;

  // Pattern 1: "Latitude:25.4452761, Longitude:84.8592364"
  const latLngRegex = /Latitude:\s*([-\d.]+),\s*Longitude:\s*([-\d.]+)/i;
  const match = str.match(latLngRegex);
  if (match) {
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return {
        isCoordinates: true,
        lat,
        lng,
        label: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        routeDestination: `${lat},${lng}`
      };
    }
  }

  // Pattern 2: "28.6139, 77.2090"
  const commaSplit = str.split(',');
  if (commaSplit.length === 2 && !isNaN(parseFloat(commaSplit[0])) && !isNaN(parseFloat(commaSplit[1]))) {
    const lat = parseFloat(commaSplit[0].replace(/[^\d.-]/g, ''));
    const lng = parseFloat(commaSplit[1].replace(/[^\d.-]/g, ''));
    if (!isNaN(lat) && !isNaN(lng)) {
      return {
        isCoordinates: true,
        lat,
        lng,
        label: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        routeDestination: `${lat},${lng}`
      };
    }
  }

  // Pattern 3: City or Address e.g. "India", "Gorakhpur"
  return {
    isCoordinates: false,
    lat: null,
    lng: null,
    label: str,
    routeDestination: str
  };
};

function ClientWithdraw() {
  const authToken = sessionStorage.getItem('authToken');
  const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
  const baseurl = rawBaseUrl.trim();

  // Primary Data State
  const [withdrawals, setWithdrawals] = useState([]);
  const [apiAnalytics, setApiAnalytics] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [serverPagination, setServerPagination] = useState(null);

  // Modals & Active Records
  const [selectedWithdraw, setSelectedWithdraw] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [rejectRemark, setRejectRemark] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  // Copy helper
  const copyToClipboard = (text, label = 'Address') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const formatShortWallet = (wallet) => {
    if (!wallet) return 'N/A';
    if (wallet.length <= 16) return wallet;
    return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
  };

  // Google Maps Turn-by-Turn GPS Directions Navigation
  const openDefineRoute = (destination) => {
    if (!destination) {
      toast.warn("Location unavailable for turn-by-turn routing");
      return;
    }
    const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    window.open(routeUrl, '_blank', 'noopener,noreferrer');
  };

  // Fetch Withdrawals from the API endpoint
  const fetchWithdrawData = useCallback(async (page = 1, perPage = itemsPerPage) => {
    setTableLoading(true);
    try {
      const response = await fetch(`${baseurl}/client/withdraw-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          page: page,
          per_page: perPage,
          status: statusFilter || undefined,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          search: searchTerm || undefined
        }),
      });

      if (response.status === 429) {
        toast.warn('Rate limit exceeded. Please wait a moment.');
        setTableLoading(false);
        return;
      }

      if (response.status === 401) {
        console.warn('API returned 401 Unauthenticated.');
        setTableLoading(false);
        return;
      }

      const result = await response.json();

      if (result) {
        if (Array.isArray(result.data)) {
          setWithdrawals(result.data);
        } else if (Array.isArray(result)) {
          setWithdrawals(result);
        }

        if (result.analytics) {
          setApiAnalytics(result.analytics);
        }

        if (result.pagination) {
          setServerPagination(result.pagination);
        }
      }
    } catch (error) {
      console.error('Fetch withdraw error:', error);
    } finally {
      setTableLoading(false);
    }
  }, [authToken, baseurl, itemsPerPage, statusFilter, fromDate, toDate, searchTerm]);

  useEffect(() => {
    fetchWithdrawData(1, itemsPerPage);
  }, [fetchWithdrawData, itemsPerPage]);

  // Client-side filtering
  const filteredWithdrawals = useMemo(() => {
    let list = [...withdrawals];

    // Status filter
    if (statusFilter) {
      const lowerStatus = statusFilter.toLowerCase();
      list = list.filter(item => {
        const itemStatus = (item.status || '').toLowerCase();
        if (lowerStatus === 'reject') return itemStatus === 'reject' || itemStatus === 'rejected';
        return itemStatus === lowerStatus;
      });
    }

    // Search filter
    if (searchTerm.trim()) {
      const query = searchTerm.trim().toLowerCase();
      list = list.filter(item => {
        const userName = (item.user_name || item.user?.name || '').toLowerCase();
        const userId = String(item.user_id || item.user?.user_id || '').toLowerCase();
        const toWallet = (item.to_address || item.user?.wallet || '').toLowerCase();
        const fromWallet = (item.from_address || '').toLowerCase();
        const txHash = (item.txn_hash || '').toLowerCase();
        return (
          userName.includes(query) ||
          userId.includes(query) ||
          toWallet.includes(query) ||
          fromWallet.includes(query) ||
          txHash.includes(query)
        );
      });
    }

    // Date range filter
    if (fromDate) {
      list = list.filter(item => {
        if (!item.date_time && !item.created_at) return true;
        const itemDate = (item.date_time || item.created_at).slice(0, 10);
        return itemDate >= fromDate;
      });
    }

    if (toDate) {
      list = list.filter(item => {
        if (!item.date_time && !item.created_at) return true;
        const itemDate = (item.date_time || item.created_at).slice(0, 10);
        return itemDate <= toDate;
      });
    }

    return list;
  }, [withdrawals, statusFilter, searchTerm, fromDate, toDate]);

  // Real-time computed Analytics Cards
  const computedAnalytics = useMemo(() => {
    const totalCount = withdrawals.length;
    let totalAmount = 0;
    let totalPaid = 0;
    let pendingCount = 0;
    let pendingAmount = 0;
    let successCount = 0;
    let successAmount = 0;
    let rejectCount = 0;
    let rejectAmount = 0;

    withdrawals.forEach(w => {
      const reqAmt = parseFloat(w.withdraw_amount) || 0;
      const paidAmt = parseFloat(w.paid_amount) || 0;
      const st = (w.status || '').toLowerCase();

      totalAmount += reqAmt;
      totalPaid += paidAmt;

      if (st === 'pending' || st === 'processing') {
        pendingCount += 1;
        pendingAmount += reqAmt;
      } else if (st === 'success') {
        successCount += 1;
        successAmount += reqAmt;
      } else if (st === 'reject' || st === 'rejected') {
        rejectCount += 1;
        rejectAmount += reqAmt;
      }
    });

    return {
      total_no: apiAnalytics?.total_no_of_withdraws || totalCount,
      total_amount: apiAnalytics ? parseFloat(apiAnalytics.total_withdraw_amount || 0) : totalAmount,
      total_paid: totalPaid,
      pending_no: apiAnalytics?.total_no_of_pending_withdraws || pendingCount,
      pending_amount: apiAnalytics ? parseFloat(apiAnalytics.total_withdraw_amount_pending || 0) : pendingAmount,
      success_no: apiAnalytics?.total_no_of_success_withdraws || successCount,
      success_amount: apiAnalytics ? parseFloat(apiAnalytics.total_withdraw_amount_success || 0) : successAmount,
      reject_no: apiAnalytics?.total_no_of_reject_withdraws || rejectCount,
      reject_amount: apiAnalytics ? parseFloat(apiAnalytics.total_withdraw_amount_reject || 0) : rejectAmount
    };
  }, [withdrawals, apiAnalytics]);

  // Server vs Client Pagination Calculations
  const isServerPaginated = Boolean(serverPagination && Number(serverPagination.total) > withdrawals.length);
  const totalCount = isServerPaginated ? Number(serverPagination.total) : filteredWithdrawals.length;
  const totalPages = isServerPaginated
    ? (Number(serverPagination.last_page) || Math.ceil(totalCount / itemsPerPage) || 1)
    : (Math.ceil(filteredWithdrawals.length / itemsPerPage) || 1);

  const paginatedList = useMemo(() => {
    if (isServerPaginated) return filteredWithdrawals;
    const start = (currentPage - 1) * itemsPerPage;
    return filteredWithdrawals.slice(start, start + itemsPerPage);
  }, [filteredWithdrawals, currentPage, itemsPerPage, isServerPaginated]);

  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages && p !== currentPage) {
      setCurrentPage(p);
      if (isServerPaginated) {
        fetchWithdrawData(p, itemsPerPage);
      }
    }
  };

  const handleItemsPerPageChange = (size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
    fetchWithdrawData(1, size);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
    fetchWithdrawData(1, itemsPerPage);
    toast.info('Filters reset');
  };

  // Open Details Modal
  const openDetailsModal = (item) => {
    setSelectedWithdraw(item);
    setShowRejectInput(false);
    setRejectRemark('');
  };



  // Handle Reject Action
  const handleRejectWithdraw = async (id) => {
    if (!rejectRemark.trim()) {
      toast.error('Please enter a rejection remark');
      return;
    }
    setIsUpdating(true);
    try {
      const response = await fetch(`${baseurl}/client/rejectWithdraw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          withdraw_id: id,
          remark: rejectRemark
        }),
      });
      const data = await response.json();
      if (data.status) {
        toast.success(data.message || 'Withdrawal rejected successfully');
        setWithdrawals(prev => prev.map(item => item.id === id ? { ...item, status: 'rejected', remark: rejectRemark } : item));
        setSelectedWithdraw(null);
      } else {
        toast.error(data.message || 'Rejection failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error during rejection');
    } finally {
      setIsUpdating(false);
    }
  };

  // Resolve best location for an item
  const getItemLocationInfo = (item) => {
    const rawLoc = item.location || item.user?.login_location || item.user?.location || item.user?.fix_address;
    return parseGpsLocation(rawLoc);
  };

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">

            {/* Page Title & Breadcrumb */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <h4 className="mb-0 text-dark fw-bold">Client Withdrawal Management</h4>
                <p className="mb-0 text-muted fs-13">
                  Monitor withdrawal requests, blockchain transaction hashes, fees deduction, and client GPS locations.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                <button
                  onClick={fetchWithdrawData}
                  className="btn btn-outline-primary btn-wave d-inline-flex align-items-center gap-2"
                  disabled={tableLoading}
                >
                  <i className={`fa fa-refresh ${tableLoading ? 'fa-spin' : ''}`}></i>
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            {/* Top KPI Summary Cards */}
            <div className="row g-3 mb-4">
              {/* Total Withdrawals */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card overflow-hidden sales-card bg-primary-gradient border-0 shadow-sm h-100">
                  <div className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                        Total Withdrawals
                      </h6>
                      <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                        All Time
                      </span>
                    </div>
                    <div className="d-flex align-items-baseline justify-content-between mt-2">
                      <h3 className="fs-24 fw-bold mb-0 text-fixed-white">
                        {computedAnalytics.total_no.toLocaleString()}
                      </h3>
                      <div className="text-end">
                        <span className="fs-12 text-fixed-white op-8 d-block">Total Volume</span>
                        <span className="fs-14 fw-bold text-fixed-white">
                          {computedAnalytics.total_amount.toFixed(4)} NMRC
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Withdrawals */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card overflow-hidden sales-card bg-warning-gradient border-0 shadow-sm h-100">
                  <div className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                        Pending / Processing
                      </h6>
                      <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                        Action Required
                      </span>
                    </div>
                    <div className="d-flex align-items-baseline justify-content-between mt-2">
                      <h3 className="fs-24 fw-bold mb-0 text-fixed-white">
                        {computedAnalytics.pending_no.toLocaleString()}
                      </h3>
                      <div className="text-end">
                        <span className="fs-12 text-fixed-white op-8 d-block">Pending Value</span>
                        <span className="fs-14 fw-bold text-fixed-white">
                          {computedAnalytics.pending_amount.toFixed(4)} NMRC
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Success / Disbursed */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card overflow-hidden sales-card bg-success-gradient border-0 shadow-sm h-100">
                  <div className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                        Success / Disbursed
                      </h6>
                      <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                        Completed
                      </span>
                    </div>
                    <div className="d-flex align-items-baseline justify-content-between mt-2">
                      <h3 className="fs-24 fw-bold mb-0 text-fixed-white">
                        {computedAnalytics.success_no.toLocaleString()}
                      </h3>
                      <div className="text-end">
                        <span className="fs-12 text-fixed-white op-8 d-block">Net Paid Out</span>
                        <span className="fs-14 fw-bold text-fixed-white">
                          {computedAnalytics.total_paid.toFixed(4)} NMRC
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rejected */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card overflow-hidden sales-card bg-danger-gradient border-0 shadow-sm h-100">
                  <div className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-2 fs-12 text-fixed-white text-uppercase fw-semibold op-8">
                        Rejected
                      </h6>
                      <span className="badge bg-white-2 text-fixed-white rounded-pill px-2 py-1 fs-11">
                        Declined
                      </span>
                    </div>
                    <div className="d-flex align-items-baseline justify-content-between mt-2">
                      <h3 className="fs-24 fw-bold mb-0 text-fixed-white">
                        {computedAnalytics.reject_no.toLocaleString()}
                      </h3>
                      <div className="text-end">
                        <span className="fs-12 text-fixed-white op-8 d-block">Rejected Amount</span>
                        <span className="fs-14 fw-bold text-fixed-white">
                          {computedAnalytics.reject_amount.toFixed(4)} NMRC
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter and Search Card */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card shadow-sm border-0">
                  <div className="card-body p-3">
                    <div className="row g-3 align-items-end">
                      {/* Search Bar */}
                      <div className="col-xl-4 col-lg-4 col-md-6 col-12">
                        <label className="form-label fs-12 text-muted mb-1 fw-semibold">
                          Quick Search
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="fa fa-search text-muted"></i>
                          </span>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="form-control border-start-0"
                            placeholder="Search client name, ID, wallet, or TX hash..."
                          />
                          {searchTerm && (
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setSearchTerm('')}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Status Filter */}
                      <div className="col-xl-2 col-lg-2 col-md-6 col-12">
                        <label className="form-label fs-12 text-muted mb-1 fw-semibold">
                          Status
                        </label>
                        <select
                          className="form-select"
                          value={statusFilter}
                          onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                          }}
                        >
                          <option value="">All Statuses</option>
                          <option value="success">Success</option>
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="reject">Rejected</option>
                        </select>
                      </div>

                      {/* From Date */}
                      <div className="col-xl-2 col-lg-2 col-md-6 col-6">
                        <label className="form-label fs-12 text-muted mb-1 fw-semibold">
                          From Date
                        </label>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => {
                            setFromDate(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="form-control"
                        />
                      </div>

                      {/* To Date */}
                      <div className="col-xl-2 col-lg-2 col-md-6 col-6">
                        <label className="form-label fs-12 text-muted mb-1 fw-semibold">
                          To Date
                        </label>
                        <input
                          type="date"
                          value={toDate}
                          onChange={(e) => {
                            setToDate(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="form-control"
                        />
                      </div>

                      {/* Reset Button */}
                      <div className="col-xl-2 col-lg-2 col-md-12 col-12 text-end">
                        <button
                          onClick={handleResetFilters}
                          className="btn btn-light w-100 border text-muted"
                          title="Reset Filters"
                        >
                          <i className="fa fa-undo me-1"></i> Reset Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Withdrawals Table Card */}
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm border-0">
                  <div className="card-header d-flex justify-content-between align-items-center bg-transparent py-3 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="card-title mb-0 fw-bold text-dark">
                        Withdrawal History
                      </h5>
                      <span className="badge bg-primary-transparent text-primary rounded-pill">
                        {totalCount} Records
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div className="d-flex align-items-center gap-1 fs-12 text-muted">
                        <span>Show:</span>
                        <select
                          className="form-select form-select-sm py-1 px-2"
                          style={{ width: '75px' }}
                          value={itemsPerPage}
                          onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      <span className="text-muted fs-12 d-none d-sm-inline">
                        Showing {paginatedList.length} of {totalCount} entries
                      </span>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    <div className="table-responsive country-table">
                      {tableLoading ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 text-muted fs-13">Fetching withdrawal records...</p>
                        </div>
                      ) : (
                        <table className="table table-hover align-middle mb-0 text-nowrap">
                          <thead className="table-light">
                            <tr>
                              <th scope="col" className="ps-3">ID</th>
                              <th scope="col">CLIENT</th>
                              <th scope="col">WITHDRAW AMOUNT</th>
                              <th scope="col">FEES & NET PAID</th>
                              <th scope="col">DESTINATION WALLET</th>
                              <th scope="col">TXN HASH</th>
                              <th scope="col">LOCATION & ROUTE</th>
                              <th scope="col">DATE & TIME</th>
                              <th scope="col">STATUS</th>
                              <th scope="col" className="text-center pe-3">ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedList.length > 0 ? (
                              paginatedList.map((item) => {
                                const locationInfo = getItemLocationInfo(item);
                                const isPending = item.status === 'pending' || item.status === 'processing';

                                return (
                                  <tr key={item.id}>
                                    {/* Record ID */}
                                    <td className="ps-3 text-muted fw-semibold fs-12">
                                      #{item.id}
                                    </td>

                                    {/* Client info */}
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <img
                                          src={item.user_profile || item.user?.profile_image || "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif"}
                                          alt={item.user_name || item.user?.name || "User"}
                                          className="rounded-circle avatar-sm border"
                                          style={{ width: "38px", height: "38px", objectFit: "cover" }}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif";
                                          }}
                                        />
                                        <div>
                                          <Link
                                            to="/clientsDetails"
                                            onClick={() => sessionStorage.setItem("clientId", item.user_id || item.user?.user_id)}
                                            className="text-primary fw-bold text-decoration-none d-block lh-1 hover-underline"
                                            title="View User Details"
                                          >
                                            {item.user_name || item.user?.name || 'Unnamed Client'}
                                          </Link>
                                          <small className="text-muted fs-11">
                                            ID: <span className="fw-semibold">#{item.user_id || item.user?.user_id || 'N/A'}</span>
                                            {item.user?.sponsor_id && (
                                              <span className="ms-1 op-7">| Sp: #{item.user.sponsor_id}</span>
                                            )}
                                          </small>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Withdraw Amount */}
                                    <td>
                                      <div className="fw-bold text-dark fs-13">
                                        {parseFloat(item.withdraw_amount || 0).toFixed(4)} {item.withdraw_in || 'NMRC'}
                                      </div>
                                      <small className="text-muted fs-11">
                                        Avail: {parseFloat(item.available_amount || 0).toFixed(4)}
                                      </small>
                                    </td>

                                    {/* Fees & Net Paid */}
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-success-transparent text-success fw-bold">
                                          {parseFloat(item.paid_amount || 0).toFixed(4)} {item.withdraw_in || 'NMRC'}
                                        </span>
                                      </div>
                                      <small className="text-muted fs-11">
                                        Fee: <span className="text-danger fw-semibold">{item.fees_deduction || 10}%</span>
                                        {item.remain_amount > 0 && ` | Rem: ${parseFloat(item.remain_amount).toFixed(4)}`}
                                      </small>
                                    </td>

                                    {/* Destination Wallet */}
                                    <td>
                                      <div className="d-flex align-items-center gap-1 font-monospace fs-12">
                                        <span title={item.to_address}>
                                          {formatShortWallet(item.to_address || item.user?.wallet)}
                                        </span>
                                        {(item.to_address || item.user?.wallet) && (
                                          <button
                                            type="button"
                                            onClick={() => copyToClipboard(item.to_address || item.user?.wallet, 'Recipient Wallet')}
                                            className="btn btn-link btn-sm p-0 text-muted ms-1"
                                            title="Copy Wallet Address"
                                          >
                                            <i className="fa fa-copy"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>

                                    {/* Txn Hash */}
                                    <td>
                                      {item.txn_hash ? (
                                        <div className="d-flex align-items-center gap-1 font-monospace fs-12">
                                          <span title={item.txn_hash}>
                                            {formatShortWallet(item.txn_hash)}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => copyToClipboard(item.txn_hash, 'Txn Hash')}
                                            className="btn btn-link btn-sm p-0 text-muted ms-1"
                                            title="Copy Txn Hash"
                                          >
                                            <i className="fa fa-copy"></i>
                                          </button>
                                          {item.txn_hash.startsWith('0x') && (
                                            <a
                                              href={`https://bscscan.com/tx/${item.txn_hash}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="btn btn-link btn-sm p-0 text-primary ms-1"
                                              title="View on BscScan"
                                            >
                                              <i className="fa fa-external-link"></i>
                                            </a>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted fs-12">Pending TX</span>
                                      )}
                                    </td>

                                    {/* Location & Route Navigation */}
                                    <td>
                                      {locationInfo ? (
                                        <div className="d-flex align-items-center gap-1">
                                          <span
                                            className="badge bg-light text-dark border text-truncate"
                                            style={{ maxWidth: '140px' }}
                                            title={locationInfo.label}
                                          >
                                            <i className="fa fa-map-marker text-danger me-1"></i>
                                            {locationInfo.label}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => openDefineRoute(locationInfo.routeDestination)}
                                            className="btn btn-sm btn-outline-info py-0 px-2"
                                            title="Open Turn-by-Turn GPS Route in Google Maps"
                                          >
                                            <i className="fa fa-location-arrow me-1"></i> Route
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="text-muted fs-12">N/A</span>
                                      )}
                                    </td>

                                    {/* Date & Time */}
                                    <td>
                                      <span className="fs-12 text-muted">
                                        {item.date_time || item.created_at?.slice(0, 19).replace('T', ' ') || 'N/A'}
                                      </span>
                                    </td>

                                    {/* Status */}
                                    <td>
                                      {item.status === 'success' ? (
                                        <span className="badge bg-success-transparent text-success rounded-pill px-2 py-1">
                                          <i className="fa fa-check-circle me-1"></i> Success
                                        </span>
                                      ) : isPending ? (
                                        <span className="badge bg-warning-transparent text-warning rounded-pill px-2 py-1">
                                          <i className="fa fa-clock-o me-1"></i> {item.status}
                                        </span>
                                      ) : (
                                        <span className="badge bg-danger-transparent text-danger rounded-pill px-2 py-1">
                                          <i className="fa fa-times-circle me-1"></i> {item.status || 'Rejected'}
                                        </span>
                                      )}
                                    </td>

                                    {/* Action Buttons */}
                                    <td className="text-center pe-3">
                                      <button
                                        type="button"
                                        onClick={() => openDetailsModal(item)}
                                        className="btn btn-sm btn-primary"
                                        title="View Full Details & GPS Location"
                                      >
                                        <i className="fa fa-eye me-1"></i> Details
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={10} className="text-center py-5">
                                  <div className="text-muted">
                                    <i className="fa fa-folder-open-o fs-30 d-block mb-2 op-4"></i>
                                    <h6>No Withdrawal Records Found</h6>
                                    <p className="fs-12 mb-0">Try clearing filters or search criteria.</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  {/* Pagination Footer */}
                  {totalCount > 0 && (
                    <div className="card-footer bg-transparent py-3 border-top">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <span className="text-muted fs-13">
                            Showing <b className="text-dark">{(currentPage - 1) * itemsPerPage + (paginatedList.length > 0 ? 1 : 0)}</b> to{' '}
                            <b className="text-dark">{Math.min(currentPage * itemsPerPage, totalCount)}</b> of{' '}
                            <b className="text-dark">{totalCount}</b> results
                          </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <div className="btn-group btn-group-sm">
                            {/* First Page Button */}
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              title="First Page"
                            >
                              <i className="fa fa-angle-double-left"></i>
                            </button>

                            {/* Previous Button */}
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              title="Previous Page"
                            >
                              <i className="fa fa-angle-left me-1"></i> Prev
                            </button>

                            {/* Page Numbers with Smart Ellipsis Window */}
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                              .map((p, idx, arr) => {
                                const prev = arr[idx - 1];
                                const showEllipsis = prev && p - prev > 1;

                                return (
                                  <React.Fragment key={p}>
                                    {showEllipsis && (
                                      <button type="button" className="btn btn-outline-secondary disabled" disabled>
                                        ...
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handlePageChange(p)}
                                      className={`btn ${currentPage === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    >
                                      {p}
                                    </button>
                                  </React.Fragment>
                                );
                              })}

                            {/* Next Button */}
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              title="Next Page"
                            >
                              Next <i className="fa fa-angle-right ms-1"></i>
                            </button>

                            {/* Last Page Button */}
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              title="Last Page"
                            >
                              <i className="fa fa-angle-double-right"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* MODAL 1: VIEW DETAILS & GPS NAVIGATION MODAL */}
        {selectedWithdraw && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)' }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header bg-primary text-white py-3">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa fa-info-circle fs-18"></i>
                    <h6 className="modal-title fw-bold text-white mb-0">
                      Withdrawal Details #{selectedWithdraw.id}
                    </h6>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedWithdraw(null)}
                  ></button>
                </div>

                <div className="modal-body p-4">
                  {/* User Profile Card */}
                  <div className="card bg-light border-0 mb-3">
                    <div className="card-body p-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
                      <div className="d-flex align-items-center gap-3">
                        <img
                          src={selectedWithdraw.user_profile || selectedWithdraw.user?.profile_image || "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif"}
                          alt="Avatar"
                          className="rounded-circle border"
                          style={{ width: "52px", height: "52px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://apiuser.ride2luxury.in/images/profileImage/avtarImage-1.avif";
                          }}
                        />
                        <div>
                          <h6 className="mb-0 fw-bold text-dark">
                            {selectedWithdraw.user_name || selectedWithdraw.user?.name || 'Unnamed Client'}
                          </h6>
                          <div className="fs-12 text-muted">
                            User ID: <span className="fw-semibold text-dark">#{selectedWithdraw.user_id || selectedWithdraw.user?.user_id}</span>
                            {selectedWithdraw.user?.sponsor_id && (
                              <span className="ms-2">| Sponsor ID: #{selectedWithdraw.user.sponsor_id}</span>
                            )}
                          </div>
                          {selectedWithdraw.user?.rank && (
                            <span className="badge bg-secondary-transparent text-secondary fs-10 mt-1">
                              Rank: {selectedWithdraw.user.rank}
                            </span>
                          )}
                        </div>
                      </div>

                      <Link
                        to="/clientsDetails"
                        onClick={() => sessionStorage.setItem("clientId", selectedWithdraw.user_id || selectedWithdraw.user?.user_id)}
                        className="btn btn-sm btn-primary"
                      >
                        <i className="fa fa-user me-1"></i> Full User Profile
                      </Link>
                    </div>
                  </div>

                  {/* Financial Breakdown Table */}
                  <div className="row g-3 mb-3">
                    <div className="col-md-6 col-12">
                      <div className="border rounded p-3 h-100">
                        <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
                          <i className="fa fa-money text-success me-2"></i> Amount Breakdown
                        </h6>
                        <div className="d-flex justify-content-between mb-2 fs-13">
                          <span className="text-muted">Available Balance:</span>
                          <span className="fw-semibold">
                            {parseFloat(selectedWithdraw.available_amount || 0).toFixed(4)} {selectedWithdraw.withdraw_in || 'NMRC'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2 fs-13">
                          <span className="text-muted">Requested Amount:</span>
                          <span className="fw-bold text-dark">
                            {parseFloat(selectedWithdraw.withdraw_amount || 0).toFixed(4)} {selectedWithdraw.withdraw_in || 'NMRC'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2 fs-13">
                          <span className="text-muted">Fees Deduction:</span>
                          <span className="text-danger fw-semibold">
                            {selectedWithdraw.fees_deduction || 10}%
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2 fs-13 border-top pt-2">
                          <span className="fw-bold text-success">Net Paid Amount:</span>
                          <span className="fw-bold text-success fs-15">
                            {parseFloat(selectedWithdraw.paid_amount || 0).toFixed(4)} {selectedWithdraw.withdraw_in || 'NMRC'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between fs-13">
                          <span className="text-muted">Remaining Balance:</span>
                          <span className="fw-semibold">
                            {parseFloat(selectedWithdraw.remain_amount || 0).toFixed(4)} {selectedWithdraw.withdraw_in || 'NMRC'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 col-12">
                      <div className="border rounded p-3 h-100">
                        <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
                          <i className="fa fa-exchange text-info me-2"></i> Blockchain Transaction
                        </h6>
                        <div className="mb-2">
                          <small className="text-muted d-block">From Vault Address:</small>
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="font-monospace fs-11 text-break">
                              {selectedWithdraw.from_address || 'N/A'}
                            </span>
                            {selectedWithdraw.from_address && (
                              <button
                                type="button"
                                onClick={() => copyToClipboard(selectedWithdraw.from_address, 'Vault Address')}
                                className="btn btn-link btn-sm p-0 ms-1 text-muted"
                              >
                                <i className="fa fa-copy"></i>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted d-block">Recipient Client Address:</small>
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="font-monospace fs-11 text-break text-primary fw-semibold">
                              {selectedWithdraw.to_address || selectedWithdraw.user?.wallet || 'N/A'}
                            </span>
                            {(selectedWithdraw.to_address || selectedWithdraw.user?.wallet) && (
                              <button
                                type="button"
                                onClick={() => copyToClipboard(selectedWithdraw.to_address || selectedWithdraw.user?.wallet, 'Recipient Wallet')}
                                className="btn btn-link btn-sm p-0 ms-1 text-muted"
                              >
                                <i className="fa fa-copy"></i>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted d-block">Transaction Hash:</small>
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="font-monospace fs-11 text-break">
                              {selectedWithdraw.txn_hash || 'Pending Broadcast'}
                            </span>
                            {selectedWithdraw.txn_hash && (
                              <div className="d-flex gap-1 ms-1">
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(selectedWithdraw.txn_hash, 'Txn Hash')}
                                  className="btn btn-link btn-sm p-0 text-muted"
                                >
                                  <i className="fa fa-copy"></i>
                                </button>
                                {selectedWithdraw.txn_hash.startsWith('0x') && (
                                  <a
                                    href={`https://bscscan.com/tx/${selectedWithdraw.txn_hash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-link btn-sm p-0 text-primary"
                                    title="View on BscScan"
                                  >
                                    <i className="fa fa-external-link"></i>
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="d-flex justify-content-between fs-12 border-top pt-2">
                          <span className="text-muted">Date & Time:</span>
                          <span className="fw-semibold">
                            {selectedWithdraw.date_time || selectedWithdraw.created_at || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Geolocation & Route Navigation Map */}
                  {(() => {
                    const locInfo = getItemLocationInfo(selectedWithdraw);
                    if (!locInfo) return null;

                    return (
                      <div className="border rounded p-3 mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                          <h6 className="fw-bold text-dark mb-0">
                            <i className="fa fa-map-marker text-danger me-2"></i> Client GPS Location & Turn-by-Turn Route
                          </h6>
                          <button
                            type="button"
                            onClick={() => openDefineRoute(locInfo.routeDestination)}
                            className="btn btn-sm btn-info text-white"
                          >
                            <i className="fa fa-location-arrow me-1"></i> Open Google Maps Route
                          </button>
                        </div>

                        <div className="row g-2 mb-3">
                          <div className="col-sm-6 col-12">
                            <small className="text-muted d-block">Detected Location Coordinates:</small>
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-bold font-monospace fs-13 text-dark">
                                {locInfo.label}
                              </span>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(locInfo.routeDestination, 'Coordinates')}
                                className="btn btn-link btn-sm p-0 text-muted"
                                title="Copy Coordinates"
                              >
                                <i className="fa fa-copy"></i>
                              </button>
                            </div>
                          </div>

                          <div className="col-sm-6 col-12">
                            <small className="text-muted d-block">Login Geolocation:</small>
                            <span className="fw-semibold fs-13 text-secondary">
                              {selectedWithdraw.user?.login_location || 'Not recorded'}
                            </span>
                            {selectedWithdraw.user?.fix_address && (
                              <span className="badge bg-light text-muted border ms-2">
                                {selectedWithdraw.user.fix_address}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Embedded Google Map Iframe */}
                        <div className="ratio ratio-16x9 rounded overflow-hidden border">
                          <iframe
                            title="Client Location Map"
                            src={`https://maps.google.com/maps?q=${encodeURIComponent(locInfo.routeDestination)}&z=14&output=embed`}
                            allowFullScreen
                            loading="lazy"
                            style={{ border: 0 }}
                          ></iframe>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Reject Remark Area */}
                  {showRejectInput && (
                    <div className="alert alert-danger mb-0">
                      <label className="form-label fw-bold mb-1">Reason for Rejection:</label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        value={rejectRemark}
                        onChange={(e) => setRejectRemark(e.target.value)}
                        placeholder="Enter explanation for declining this withdrawal..."
                      />
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => setShowRejectInput(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRejectWithdraw(selectedWithdraw.id)}
                          disabled={isUpdating}
                        >
                          {isUpdating ? 'Rejecting...' : 'Confirm Rejection'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer bg-light py-2">
                  {(selectedWithdraw.status === 'pending' || selectedWithdraw.status === 'processing') && !showRejectInput && canModuleEdit('withdrawals') && (
                    <>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => setShowRejectInput(true)}
                      >
                        <i className="fa fa-times me-1"></i> Reject Withdrawal
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setSelectedWithdraw(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}



        <ToastContainer position="top-right" autoClose={3000} />
      </Header>

      <Footer />
    </>
  );
}

export default ClientWithdraw;